import {AuthorityType, ContentType, CountryCode, Document, NationalOrganisationCode, OrganisationCode} from "./document.model";
import {
  AUTHORITY_LABELS,
  authorityLabel,
  CONTENT_LABELS,
  COUNTRY_LABELS,
  ORGANISATION_LABELS,
  originCodeLabel
} from "~/app/components/document/document.labels";

export type OriginCode =
  | CountryCode
  | OrganisationCode
  | NationalOrganisationCode;

export type DocumentationFilter = {
  origin?: OriginCode;
  authority?: AuthorityType;
  content?: ContentType;
};

export const COUNTRY_CODES = Object.keys(COUNTRY_LABELS) as CountryCode[];

export const ORGANISATION_CODES = Object.keys(ORGANISATION_LABELS) as OrganisationCode[];

export const AUTHORITY_TYPES = Object.keys(AUTHORITY_LABELS) as AuthorityType[];

export const CONTENT_TYPES = Object.keys(CONTENT_LABELS) as ContentType[];

export function isOriginCode(value: string | null): value is OriginCode {
  return !!value &&
    (COUNTRY_CODES.includes(value as CountryCode) ||
      ORGANISATION_CODES.includes(value as OrganisationCode));
}

export function isAuthorityType(value: string | null): value is AuthorityType {
  return !!value &&
    AUTHORITY_TYPES.includes(value as AuthorityType);
}

export function isContentType(value: string | null): value is ContentType {
  return !!value &&
    CONTENT_TYPES.includes(value as ContentType);
}

export function parseDocumentationFilter(
  searchParams: URLSearchParams
): DocumentationFilter {

  const rawOrigin = searchParams.get("origin");
  const rawAuthority = searchParams.get("authority");
  const rawContent = searchParams.get("content"); // 🔥 neu

  return {
    origin: isOriginCode(rawOrigin) ? rawOrigin : undefined,
    authority: isAuthorityType(rawAuthority) ? rawAuthority : undefined,
    content: isContentType(rawContent) ? rawContent : undefined
  };
}

export function extractAvailableCodes(documents: Document[]): OriginCode[] {
  const set = new Set<OriginCode>();
  documents.forEach(d => set.add(d.origin.code));

  return Array.from(set).sort((a, b) =>
    originCodeLabel(a).localeCompare(originCodeLabel(b), "de")
  );
}

export function extractAvailableAuthorities(documents: Document[]): AuthorityType[] {
  const set = new Set<AuthorityType>();
  documents.forEach(d => set.add(d.authorityType));

  return Array.from(set).sort((a, b) =>
    authorityLabel(a).localeCompare(authorityLabel(b), "de")
  );
}

export function extractAvailableContentTypes(
  documents: Document[]
): ContentType[] {

  const set = new Set<ContentType>();

  documents.forEach(d =>
    d.contentTypes?.forEach(ct => set.add(ct))
  );

  const values = Array.from(set);

  return values.sort((a, b) => {

    if (a === "CITIZENSHIP") return -1;
    if (b === "CITIZENSHIP") return 1;

    return CONTENT_LABELS[a].localeCompare(
      CONTENT_LABELS[b], "de"
    );
  });
}

export const DOCUMENTATION_QUERY_KEYS = {
  origin: "origin",
  authority: "authority",
  content: "content"
} as const;
